import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetCompanyGroup, updateProps } from '../../contextRedux/CompanyGroup/CompanyGroupModel';
import { setCompanyGroups } from '../../contextRedux/CompanyGroup/CompanyGroupStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import CompanyGroupService from '../../services/companyGroup/CompanyGroupService';
import { RootState } from '../../store';
import CompanyGroupModal from './CompanyGroupModal/CompanyGroupModal';
import CompanyGroupTable from './CompanyGroupTable/CompanyGroupTable';

interface CompanyGroupProps {}

const CompanyGroup: React.FC<CompanyGroupProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.companyGroup.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.companyGroup.props.skip);
  const take = useSelector((state: RootState) => state.models?.companyGroup.props.take);
  const refresh = useSelector((state: RootState) => state.models?.companyGroup.props.refresh);

  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    isFetching: isCompanyGroupsLoading,
    isError: isErrorOnFetchCompanyGroups,
    error: companyGroupsError,
    refetch,
  } = useQuery({
    queryKey: ['companyGroups', searchTerm, skip, take, refresh],
    queryFn: () =>
      CompanyGroupService.search({
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data: ControllerResponse<any>) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setCompanyGroups(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleReset = () => {
    dispatch(
      resetCompanyGroup({
        isModalVisible: true,
        refresh: true,
        searchTerm,
        skip,
        take,
      })
    );
  };

  if (isCompanyGroupsLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchCompanyGroups) {
    return <Alert message={companyGroupsError.message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Grupuri Afiliate Clienti</h1>
      <AnimateButton onClick={() => handleReset()} style={{ marginBottom: 10 }}>
        Adauga
      </AnimateButton>
      <CompanyGroupModal />
      <CompanyGroupTable />
    </>
  );
};

export default CompanyGroup;
